// 工具函数
const MathUtils = {
  distance: (p1, p2) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)),
  unitVector: (p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = MathUtils.distance(p1, p2);
    return length > 0 ? { x: dx / length, y: dy / length } : { x: 0, y: 0 };
  },
  perpendicularVector: vec => ({ x: -vec.y, y: vec.x }),
  lerp: (a, b, t) => a + (b - a) * t,
  // 计算沿路径的点
  getPointAlongPath: (pathPoints, progress) => {
    if (pathPoints.length < 2) return pathPoints[0] || { x: 0, y: 0 };

    // 计算总路径长度
    let totalLength = 0;
    const segmentLengths = [];

    for (let i = 1; i < pathPoints.length; i++) {
      const length = MathUtils.distance(pathPoints[i - 1], pathPoints[i]);
      segmentLengths.push(length);
      totalLength += length;
    }

    // 计算目标距离
    const targetDistance = progress * totalLength;

    // 找到目标线段
    let accumulatedDistance = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
      if (accumulatedDistance + segmentLengths[i] >= targetDistance) {
        const segmentProgress = (targetDistance - accumulatedDistance) / segmentLengths[i];
        const start = pathPoints[i];
        const end = pathPoints[i + 1];

        return new Point(
          MathUtils.lerp(start.x, end.x, segmentProgress),
          MathUtils.lerp(start.y, end.y, segmentProgress)
        );
      }
      accumulatedDistance += segmentLengths[i];
    }

    // 如果超出路径，返回终点
    return pathPoints[pathPoints.length - 1];
  }
};

// 点类
class Point {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }

  clone() {
    return new Point(this.x, this.y);
  }

  addVector(vec) {
    return new Point(this.x + vec.x, this.y + vec.y);
  }
}

// 移动箭头类
class MovingArrow {
  constructor(path, size, color, speedFactor = 1) {
    this.path = path; // 关联的路径
    this.size = size; // 箭头大小
    this.color = color; // 箭头颜色
    this.progress = Math.random(); // 沿路径的进度 (0-1)
    this.speedFactor = speedFactor; // 速度因子
  }

  // 更新箭头位置
  update(deltaTime, baseSpeed) {
    // 根据时间和速度更新进度
    const speed = 0.0001 * baseSpeed * this.speedFactor;
    this.progress = (this.progress + deltaTime * speed) % 1;
  }

  // 获取当前位置和方向
  getCurrentPosition() {
    // 当前位置
    const currentPoint = MathUtils.getPointAlongPath(this.path.points, this.progress);

    // 稍微向前一点的位置，用于确定方向
    const nextProgress = Math.min(1, this.progress + 0.01);
    const nextPoint = MathUtils.getPointAlongPath(this.path.points, nextProgress);

    return { currentPoint, nextPoint };
  }

  // 绘制箭头
  draw(ctx) {
    const { currentPoint, nextPoint } = this.getCurrentPosition();
    const dir = MathUtils.unitVector(currentPoint, nextPoint);
    if (dir.x === 0 && dir.y === 0) return;

    const perp = MathUtils.perpendicularVector(dir);

    const tip = currentPoint;
    const leftWing = tip.addVector({
      x: -dir.x * this.size - perp.x * this.size * 0.5,
      y: -dir.y * this.size - perp.y * this.size * 0.5
    });
    const rightWing = tip.addVector({
      x: -dir.x * this.size + perp.x * this.size * 0.5,
      y: -dir.y * this.size + perp.y * this.size * 0.5
    });

    ctx.save();
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(tip.x, tip.y);
    ctx.lineTo(leftWing.x, leftWing.y);
    ctx.lineTo(rightWing.x, rightWing.y);
    ctx.closePath();
    ctx.fill();

    const shaftLength = this.size * 0.7;
    const shaftStart = tip.addVector({
      x: -dir.x * shaftLength - perp.x * this.size * 0.2,
      y: -dir.y * shaftLength - perp.y * this.size * 0.2
    });
    const shaftEnd = tip.addVector({
      x: -dir.x * shaftLength + perp.x * this.size * 0.2,
      y: -dir.y * shaftLength + perp.y * this.size * 0.2
    });

    ctx.beginPath();
    ctx.moveTo(shaftStart.x, shaftStart.y);
    ctx.lineTo(shaftEnd.x, shaftEnd.y);
    ctx.lineWidth = Math.max(1, Math.round(this.size * 0.25));
    ctx.strokeStyle = this.color;
    ctx.stroke();
    ctx.restore();
  }
}

// 路径类
class Path {
  constructor(color, width = 4) {
    this.points = [];
    this.arrows = []; // 移动箭头数组
    this.color = color;
    this.width = width;
    this.lastUpdateTime = 0; // 上次更新时间
  }

  addPoint(point) {
    this.points.push(point);
    return this;
  }

  addPoints(points) {
    points.forEach(point => this.addPoint(point));
    return this;
  }

  // 生成移动箭头
  generateArrows(density, size) {
    this.arrows = [];
    const arrowCount = Math.max(1, density);

    // 创建多个箭头，分布在不同进度位置
    for (let i = 0; i < arrowCount; i++) {
      // 箭头进度均匀分布
      const progress = (i / arrowCount) % 1;
      // 随机速度因子，使箭头移动有差异
      const speedFactor = 0.8 + Math.random() * 0.4;

      this.arrows.push(new MovingArrow(this, size, this.color, speedFactor));

      // 设置初始进度
      this.arrows[i].progress = progress;
    }

    return this;
  }

  // 更新所有箭头位置
  updateArrows(deltaTime, baseSpeed) {
    this.arrows.forEach(arrow => {
      arrow.update(deltaTime, baseSpeed);
    });
  }

  drawPath(ctx) {
    if (this.points.length < 2) return;

    ctx.save();
    ctx.strokeStyle = this.color;
    ctx.lineWidth = this.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(this.points[0].x, this.points[0].y);
    for (let i = 1; i < this.points.length; i++) {
      ctx.lineTo(this.points[i].x, this.points[i].y);
    }
    ctx.stroke();
    ctx.restore();
  }

  drawArrows(ctx) {
    this.arrows.forEach(arrow => arrow.draw(ctx));
  }

  drawStartMarker(ctx) {
    if (this.points.length === 0) return;

    const startPoint = this.points[0];
    ctx.save();
    ctx.beginPath();
    ctx.arc(startPoint.x, startPoint.y, 8, 0, Math.PI * 2);
    ctx.fillStyle = this.color;
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  getMergePoint() {
    return this.points.length > 3 ? this.points[3] : null;
  }
}

// 颜色管理器
class ColorManager {
  constructor() {
    this.baseColors = [
      '#3B82F6',
      '#10B981',
      '#EF4444',
      '#8B5CF6',
      '#F59E0B',
      '#EC4899',
      '#06B6D4',
      '#6366F1',
      '#14B8A6',
      '#F43F5E',
      '#84CC16',
      '#64748B'
    ];
    this.usedColors = new Set();
  }

  getRandomUnusedColor() {
    if (this.usedColors.size >= this.baseColors.length) {
      this.usedColors.clear();
    }

    let color;
    do {
      color = this.baseColors[Math.floor(Math.random() * this.baseColors.length)];
    } while (this.usedColors.has(color));

    this.usedColors.add(color);
    return color;
  }

  reset() {
    this.usedColors.clear();
  }
}

// 路径生成器
class PathGenerator {
  constructor(canvasWidth, canvasHeight, endPoint) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.endPoint = endPoint;
    this.midPointCount = 2;
  }

  generatePathPoints(startY, mergePoint) {
    const points = [new Point(50, startY)];

    for (let i = 0; i < this.midPointCount; i++) {
      const progress = (i + 1) / (this.midPointCount + 1);
      points.push(
        new Point(
          150 + Math.random() * 100 + progress * (mergePoint.x - 250),
          startY + (mergePoint.y - startY) * progress + (Math.random() * 60 - 30)
        )
      );
    }

    points.push(mergePoint);
    points.push(...this.generatePostMergePoints(mergePoint));

    return points;
  }

  generatePostMergePoints(mergePoint) {
    return [
      new Point(
        MathUtils.lerp(mergePoint.x, this.endPoint.x, 0.3) + (Math.random() * 30 - 15),
        MathUtils.lerp(mergePoint.y, this.endPoint.y, 0.3) + (Math.random() * 30 - 15)
      ),
      new Point(
        MathUtils.lerp(mergePoint.x, this.endPoint.x, 0.6) + (Math.random() * 30 - 15),
        MathUtils.lerp(mergePoint.y, this.endPoint.y, 0.6) + (Math.random() * 30 - 15)
      ),
      this.endPoint
    ];
  }

  generateMergePoint() {
    return new Point(
      this.canvasWidth * 0.65 + Math.random() * this.canvasWidth * 0.1,
      this.canvasHeight / 2 + (Math.random() * 80 - 40)
    );
  }

  generateStartY(index, totalPaths) {
    return 100 + (index / (totalPaths - 1 || 1)) * (this.canvasHeight - 200);
  }
}

// 路径管理器类
export class PathManager {
  constructor({ canvas, ctx }) {
    this.canvas = canvas;
    this.ctx = ctx;

    this.paths = [];
    this.isDrawing = false;
    this.lastTimestamp = 0; // 上次绘制时间戳

    this.config = {
      arrowDensity: 2,
      arrowSize: 16,
      pathCount: 3,
      animationSpeed: 2,
      endPoint: new Point(this.canvas.width - 50, this.canvas.height / 2)
    };

    this.colorManager = new ColorManager();
    this.pathGenerator = new PathGenerator(
      this.canvas.width,
      this.canvas.height,
      this.config.endPoint
    );
  }

  addPath(path, color = null, lineWidth = 6) {
    const pathColor = color || this.colorManager.getRandomUnusedColor();

    if (path instanceof Path) {
      path.color = pathColor;
      path.width = lineWidth;
    } else {
      path = new Path(pathColor, lineWidth).addPoints(path);
    }

    path.generateArrows(this.config.arrowDensity, this.config.arrowSize);
    this.paths.push(path);
    return path;
  }

  updateArrows() {
    this.paths.forEach(path => {
      path.generateArrows(this.config.arrowDensity, this.config.arrowSize);
    });
  }

  drawGrid() {
    const hStep = 50;
    const vStep = 50;

    this.ctx.save();
    this.ctx.strokeStyle = '#e5e7eb';
    this.ctx.lineWidth = 1;

    for (let y = 0; y < this.canvas.height; y += hStep) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(this.canvas.width, y);
      this.ctx.stroke();
    }

    for (let x = 0; x < this.canvas.width; x += vStep) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.canvas.height);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  drawEndMarker() {
    const { endPoint } = this.config;

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect(endPoint.x - 10, endPoint.y - 10, 20, 20);
    this.ctx.fillStyle = '#374151';
    this.ctx.fill();
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
    this.ctx.restore();
  }

  // 绘制所有内容并更新动画
  draw(timestamp) {
    // 计算时间差（毫秒）
    const deltaTime = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    // 清空画布
    // this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // 绘制背景网格
    // this.drawGrid();

    // 更新并绘制所有路径和箭头
    this.paths.forEach(path => {
      // 更新箭头位置
      path.updateArrows(deltaTime, this.config.animationSpeed);

      // 绘制路径
      path.drawPath(this.ctx);

      // 绘制箭头
      path.drawArrows(this.ctx);

      // 绘制起点标记
      // path.drawStartMarker(this.ctx);
    });

    // 绘制终点标记
    // this.drawEndMarker();
  }
}
